import jwtDecode from "jwt-decode";
import { create } from "zustand";

import { catchAsync } from "@/v1/utils/errorHandler";
import { queryClient } from "@/v1/utils/queryClient";
import { postTokenToParent } from "@/v2/utils/postTokenToParent";

import { loginService } from "../service/authService";
import * as service from "../service/teamMembersService";
import { IJwtMember } from "../types/Auth/IJwtMember.interface";
import { IMember } from "../types/TeamMembers/Member.interface";
import { addBearerToken } from "../utils/apiClient";
import { authentication } from "../utils/authentication";

interface IUseUserStore {
  user?: IJwtMember | null;
  impersonatedUser?: IJwtMember | null;
  activeUser?: IJwtMember | null;
  activeRoles: string[];
  activePermissions: string[];
  isAdmin: boolean;
  login: () => Promise<void | boolean>;
  logout: () => Promise<void>;
  impersonateUser: (member: IMember) => Promise<void>;
  removeImpersonatedUser: () => void;
}

export const useUserStore = create<IUseUserStore>((set) => {
  let activeUserJwt = "";

  return {
    user: null,
    impersonatedUser: null,
    isAdmin: false,
    activeRoles: [],
    activePermissions: [],
    impersonateUser: catchAsync(async (member: IMember) => {
      const { data } = await service.impersonateUser(member.id);

      queryClient.clear();

      const impersonatedUser = jwtDecode(data.jwt) as IJwtMember;

      addBearerToken(data.jwt);

      set({
        impersonatedUser,
      });
    }),
    removeImpersonatedUser: () => {
      queryClient.clear();
      addBearerToken(activeUserJwt);
      set({ impersonatedUser: null });
    },
    logout: catchAsync(async () => {
      set({ user: null });
      authentication.logout();
    }),
    login: catchAsync(async () => {
      const oktaLocalData = localStorage.getItem("okta-token-storage");

      if (oktaLocalData) {
        const oktaData = JSON.parse(oktaLocalData);

        addBearerToken(oktaData.accessToken?.value);

        const res = await loginService();

        activeUserJwt = res.data.jwt;

        postTokenToParent(res.data.jwt);
        addBearerToken(res.data.jwt);

        const user = jwtDecode(res.data.jwt) as IJwtMember;

        queryClient.clear();

        set({ user });

        return true;
      }
    }),
  };
});

useUserStore.subscribe((state, prevState) => {
  const activePermissions = state.impersonatedUser
    ? state.impersonatedUser.permissions
    : state.user?.permissions;
  const activeRoles = state.impersonatedUser ? state.impersonatedUser.roles : state.user?.roles;

  if (prevState.activeUser && prevState.activeUser === state.activeUser) return;

  useUserStore.setState({
    activePermissions,
    activeRoles,
    activeUser: state.impersonatedUser || state.user,
    isAdmin: state.impersonatedUser?.isAdmin || state.user?.isAdmin,
  });
});
